// Developer: Ankit Gupta
/**
 * Orion's greeting in the top bar while someone works in Orion (24 Oct 2026): "Hello Ankit, I'm
 * Orion. How can I help you today?", with a speaker that says it aloud.
 *
 * The voice is a studio recording made with ElevenLabs, one per language, shipped with the app
 * (public/audio/orion-greeting-en.mp3, -ar.mp3: "Bella" in English, "Layan", a Saudi voice, in
 * Arabic, saying "Orion" as the English does), so playing it costs nothing and needs no server.
 * The recording says the greeting without the person's name, which only the text carries.
 * Should a recording not load, the browser's own voice reads the text instead. A second click
 * stops it.
 */
import { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import { PauseOutlined, SoundOutlined } from '@ant-design/icons';
import { useAuth } from '@/auth/useAuthHook';
import { useLanguage, useT } from '@/i18n';
import { OrionSparkle } from './OrionIcon';

const RECORDING = { en: '/audio/orion-greeting-en.mp3', ar: '/audio/orion-greeting-ar.mp3' } as const;
const canSpeak = typeof window !== 'undefined' && 'speechSynthesis' in window && typeof SpeechSynthesisUtterance !== 'undefined';

export function OrionGreeting() {
  const { t } = useT('chat');
  const lang = useLanguage() === 'ar' ? 'ar' : 'en';
  const first = (useAuth().user?.firstName ?? '').trim().split(/\s+/)[0] ?? '';
  const text = first ? t('greeting.hello', { name: first }) : t('greeting.helloNoName');
  const [speaking, setSpeaking] = useState(false);
  const audio = useRef<HTMLAudioElement | null>(null);
  useEffect(() => () => { audio.current?.pause(); if (canSpeak) window.speechSynthesis.cancel(); }, []);

  // the browser's voice, only when the recording cannot be played
  const readAloud = () => {
    if (!canSpeak) { setSpeaking(false); return; }
    const u = new SpeechSynthesisUtterance(t('greeting.helloNoName'));
    u.lang = lang === 'ar' ? 'ar-SA' : 'en-GB';
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(u);
  };
  const stop = () => {
    audio.current?.pause();
    if (audio.current) audio.current.currentTime = 0;
    if (canSpeak) window.speechSynthesis.cancel();
    setSpeaking(false);
  };
  const play = () => {
    if (speaking) { stop(); return; }
    const a = audio.current && audio.current.dataset.lang === lang ? audio.current : new Audio(RECORDING[lang]);
    a.dataset.lang = lang;
    audio.current = a;
    a.currentTime = 0;
    a.onended = () => setSpeaking(false);
    a.onerror = () => readAloud();
    setSpeaking(true);
    a.play().catch(() => readAloud());
  };
  return (
    <div className="dp-topbar-greeting" aria-live="off">
      <OrionSparkle />
      <span className="dp-topbar-greeting-text">{text}</span>
      <Tooltip title={speaking ? t('greeting.stop') : t('greeting.listen')}>
        <button type="button" className={`dp-topbar-greeting-speak${speaking ? ' is-on' : ''}`} onClick={play} aria-label={speaking ? t('greeting.stop') : t('greeting.listen')} aria-pressed={speaking}>
          {speaking ? <PauseOutlined /> : <SoundOutlined />}
        </button>
      </Tooltip>
    </div>
  );
}
